import { Core } from "@faire/design-tokens";
import { getBrandToken } from "@faire/help-center-utils/serialized-data/getBrand";
import { getRetailerToken } from "@faire/help-center-utils/serialized-data/getRetailer";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Input } from "@faire/web--source/slate-labs/Input";
import { Button } from "@faire/web--source/slate/Button";
import { TextArea } from "@faire/web--source/slate/Input/TextArea";
import { FormField, Row } from "@faire/web--source/slate/Layout";
import { LoadingSpinner } from "@faire/web--source/slate/LoadingSpinner";
import { Spacer } from "@faire/web--source/slate/spacing";
import { DefaultTheme } from "@faire/web--source/slate/Theme";
import { ThemeProvider } from "@faire/web--source/slate/Theme/ThemeProvider";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackHelpCenterEditAccountEmailClick } from "@faire/web-api--source/events/helpCenter/click/editAccountEmail";
import { Form, useFormikContext } from "formik";
import React, { useEffect, useState } from "react";

import { useEditSettingsLink } from "../../hooks/useEditSettingsLink";

import { AttachmentFormField } from "./AttachmentFormField";
import { IEmailUsFormValues } from "./utils";

interface ITicketFormProps {
  attachment: File | undefined;
  setAttachment: (attachment: File | undefined) => void;
  isLoading: boolean;
  errorMessage?: string;
  setErrorMessage: (message: string) => void;
}

export const TicketForm: React.FC<ITicketFormProps> = ({
  attachment,
  setAttachment,
  isLoading,
  errorMessage,
  setErrorMessage,
}) => {
  const { errors, touched, getFieldProps, isValid } =
    useFormikContext<IEmailUsFormValues>();
  const { strictLocalize } = useStrictLocalization();
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const editSettingsLink = useEditSettingsLink();

  useEffect(() => {
    if (!isValid && isSubmitClicked) {
      setIsSubmitClicked(false);
      setErrorMessage(
        strictLocalize({
          defaultMessage: "Complete all required fields to submit this request",
        })
      );
    }
    if (isValid) {
      setErrorMessage("");
    }
  }, [isValid, isSubmitClicked, strictLocalize, setErrorMessage]);

  const handleEditSettingsClick = () => {
    trackHelpCenterEditAccountEmailClick(
      getRetailerToken() ?? "",
      getBrandToken() ?? ""
    );
  };

  return (
    <Form style={{ width: "100%" }}>
      <FormField $name="name">
        <Input
          label={strictLocalize({
            defaultMessage: "Your name*",
            description: {
              img: "https://cdn.faire.com/fastly/1e256716ba41dfec73588ed58bb78d2d7cc468805356f8c4237ad2b267e1f223.png",
            },
          })}
          error={touched.name ? errors.name : false}
          {...getFieldProps("name")}
        />
      </FormField>
      <Spacer height="3x" />
      <FormField $name="email">
        <Input
          type="email"
          label={strictLocalize({
            defaultMessage: "Account email",
            description: {
              text: "Email address associated with your Faire account",
            },
          })}
          error={touched.email && errors.email}
          {...getFieldProps("email")}
          disabled
          sideCta={
            <Button
              variant="tertiary"
              linkTo={editSettingsLink}
              onClick={handleEditSettingsClick}
              target="_blank"
            >
              <StrictLocalMsg
                defaultMessage="Edit settings"
                description={{
                  text: "Edit the email address associated with your Faire account",
                }}
              />
            </Button>
          }
        />
      </FormField>
      <Spacer height="3x" />
      <FormField $name="subject">
        <Input
          data-test-id="subject-input"
          label={strictLocalize({
            defaultMessage: "Subject*",
            description: {
              img: "https://cdn.faire.com/fastly/1e256716ba41dfec73588ed58bb78d2d7cc468805356f8c4237ad2b267e1f223.png",
            },
          })}
          error={touched.subject && errors.subject}
          {...getFieldProps("subject")}
        />
      </FormField>
      <Spacer height="3x" />
      <FormField $name="orderNumber">
        <Input
          data-test-id="order-number-input"
          label={strictLocalize({
            defaultMessage: "Order number (optional)",
            description: {
              img: "https://cdn.faire.com/fastly/1e256716ba41dfec73588ed58bb78d2d7cc468805356f8c4237ad2b267e1f223.png",
            },
          })}
          error={touched.orderNumber && errors.orderNumber}
          {...getFieldProps("orderNumber")}
        />
      </FormField>
      <Spacer height="3x" />
      <FormField $name="description">
        <ThemeProvider
          theme={{
            ...DefaultTheme,
            input: {
              ...DefaultTheme.input,
              helper: {
                ...DefaultTheme.input.helper,
                color: Core.text.subdued,
              },
            },
          }}
        >
          <TextArea
            data-test-id="description-input"
            style={{ height: "120px" }}
            maxLength={4000}
            helper={strictLocalize({
              defaultMessage: "Describe the issue in more detail",
              description: {
                img: "https://cdn.faire.com/fastly/1e256716ba41dfec73588ed58bb78d2d7cc468805356f8c4237ad2b267e1f223.png",
              },
            })}
            label={strictLocalize({
              defaultMessage: "Message*",
              description: {
                img: "https://cdn.faire.com/fastly/1e256716ba41dfec73588ed58bb78d2d7cc468805356f8c4237ad2b267e1f223.png",
              },
            })}
            error={touched.description && errors.description}
            {...getFieldProps("description")}
          />
        </ThemeProvider>
      </FormField>
      <Spacer height="3x" />
      <FormField $name="websiteUrl">
        <Input
          label={strictLocalize({
            defaultMessage: "Faire URL (optional)",
            description: {
              img: "https://cdn.faire.com/fastly/1e256716ba41dfec73588ed58bb78d2d7cc468805356f8c4237ad2b267e1f223.png",
            },
          })}
          error={touched.websiteUrl && errors.websiteUrl}
          {...getFieldProps("websiteUrl")}
        />
        <Spacer height="1x" />
        <Typography color={Core.text.subdued} variant="labelSansRegular">
          <StrictLocalMsg
            defaultMessage="Provide issue URL if applicable"
            description={{
              img: "https://cdn.faire.com/fastly/1e256716ba41dfec73588ed58bb78d2d7cc468805356f8c4237ad2b267e1f223.png",
            }}
          />
        </Typography>
      </FormField>
      <Spacer height="3x" />
      <AttachmentFormField
        attachment={attachment}
        setAttachment={setAttachment}
      />
      <Spacer height="3x" />
      <Row align="center">
        <Button
          data-test-id="submit-btn"
          type="submit"
          disabled={isLoading}
          onClick={() => setIsSubmitClicked(true)}
        >
          <StrictLocalMsg
            defaultMessage="Submit"
            description={{
              img: "https://cdn.faire.com/fastly/1e256716ba41dfec73588ed58bb78d2d7cc468805356f8c4237ad2b267e1f223.png",
            }}
          />
        </Button>
        <Spacer width="1x" />
        {isLoading ? <LoadingSpinner size="medium" /> : null}
      </Row>
      {errorMessage && !isLoading ? (
        <>
          <Spacer height="1/2x" />
          <Typography color={Core.text.critical}>{errorMessage}</Typography>
        </>
      ) : null}
    </Form>
  );
};
